/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { FC, useEffect, useMemo, useState } from 'react';
import Form from '~/components/WizardForm';
import { Col, Input, Modal, Row, Table, Tabs, Tag, Typography } from 'antd';
import Button from '~/components/Button';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import colors from '~/constants/colors';
import produce from 'immer';
import dayjs from 'dayjs';
import WizardForm from '~/components/WizardForm';
import {
  useGetApplication,
  useGetPayloadWithProvided,
  useSaveStep,
  useSubmitStep,
} from '~/controllers/wizard';
import { Stage } from '~/types/Application';
import { Draft } from '~/types/Draft';
import { getDecodedJwt, isAgent } from '~/utils/getDecodedJwt';
import { isEmpty } from 'lodash';
import ManualScreeningAction from './components/ManualScreeningAction';
import AutomatedScreeningResult from './components/AutomatedScreeningResult';
import ManualScreeningResultsView from './components/ManualScreeningResultView';
import AgentStatus from '~/pages/ksa/review/screeningResult/AgentStatus';
import { retriggerScreening } from '~/api/review';
import { checkApplicationNeedsFlag, getFlaggedFields } from '~/utils/checkApplicationNeedsFlag';
import { useDirection } from '~/hooks/i18nHooks';

/** Shape of a single high-risk reason tag used by the screening result page. */
interface HighRiskReason {
  label: string;
  value: string;
  status: 'error' | 'default';
}

const ScreeningResult: FC = () => {
  const { t } = useTranslation('ksaReview');
  const dir = useDirection();
  const [highResikReasons, setHighRiskReasons] = useState<any>(() => [
    { label: t('Business Structure from Thiqah'), value: 'PARTIES_11111' },
    { label: t('Business Activity (MCC)'), value: 'MCC_11111' },
    { label: t('Master MID Availability'), value: 'MASTER_11111' },
    { label: t('IBAN Verification Result'), value: 'IBAN_11111' },
    { label: t('Compliance Questions'), value: 'IS_PEP_11111' },
    { label: t('Screening Match'), value: 'AMLOCK_11111' },
    { label: t('Missing/Wrong Retrieved Data'), value: 'MISSING_WRONG_11111' },
  ]);
  const [form] = WizardForm.useForm();
  const { data, isFetched, isLoading, refetch }: any = useGetApplication();
  const submitMutation = useSubmitStep();
  const saveMutation = useSaveStep();
  const decodedToken = getDecodedJwt();
  const payload = useGetPayloadWithProvided();
  const screeningResult = payload?.reviewScreeningResult || {};
  const newReviewIndex = screeningResult?.manual?.length ?? 0;
  const lastAutomatedResolution =
    screeningResult?.automated &&
    !isEmpty(screeningResult?.automated) &&
    screeningResult?.automated[screeningResult?.automated?.length - 1]?.resolution;
  const splAddress = data?.nationalAddressMissingInformation;

  // Computed before useEffect so the closure in the effect captures the up-to-date derived values.
  // hasMissingWrongData drives the auto-status of the MISSING_WRONG_11111 high-risk reason tag.
  const hasMissingWrongData = useMemo(() => checkApplicationNeedsFlag(payload), [payload]);
  // flaggedFields is the row data for the "Missing/Wrong Retrieved Data" tab table.
  const flaggedFields = useMemo(() => getFlaggedFields(payload), [payload]);

  useEffect(() => {
    setTimeout(() => {
      if (data?.stage !== 'ksaReview') {
        const rawValue = form.getFieldValue(['reviewScreeningResult', 'highRiskReasons']);
        const parsed = rawValue ? JSON.parse(rawValue) : [];
        const highRiskReasons = highResikReasons?.map((item: any) => {
          if (item.value === 'MISSING_WRONG_11111') {
            // @ts-ignore
            return { ...item, status: hasMissingWrongData ? 'error' : 'default' };
          }
          if (parsed?.includes(item?.value)) {
            // @ts-ignore
            return { ...item, status: 'error' };
          }
          // @ts-ignore
          return { ...item, status: 'default' };
        });
        setHighRiskReasons(highRiskReasons);
      }
    }, 600);

    if (data?.stage === 'ksaReview') {
      refetch();
      const appReasons = data?.highRiskReasons;
      const highRiskReasons = highResikReasons?.map((item: any) => {
        // Same auto-flag logic for the ksaReview stage path
        if (item.value === 'MISSING_WRONG_11111') {
          // @ts-ignore
          return { ...item, status: hasMissingWrongData ? 'error' : 'default' };
        }
        if (appReasons?.includes(item?.value)) {
          // @ts-ignore
          return { ...item, status: 'error' };
        }
        // @ts-ignore
        return { ...item, status: 'default' };
      });
      setHighRiskReasons(highRiskReasons);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched, isLoading, data, hasMissingWrongData]);

  const [isFormVisible, setFormVisible] = useState<boolean>(false);
  const [isTriggeringScreening, setIsTriggeringScreening] = useState(false);

  const handleTriggerScreening = async () => {
    setIsTriggeringScreening(true);
    try {
      // Payload key is a placeholder; backend team will update the key accordingly
      await retriggerScreening({ screeningData: {} });
      await refetch();
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.description ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        t('An error occurred while triggering the screening. Please try again.');
      Modal.error({
        title: t('Trigger Screening Failed'),
        content: apiMessage,
      });
    } finally {
      setIsTriggeringScreening(false);
    }
  };

  if (!payload) return null;

  return (
    <StyledTabs defaultActiveKey="1">
      <Tabs.TabPane tab={<WideTabLabel>{t('Screening Results')}</WideTabLabel>} key="1">
        <Container className="ScreeningResult">
          <WizardForm<Draft<'reviewScreeningResult'>>
            stage={Stage.ksaReview}
            payloadKey="reviewScreeningResult"
            form={form}
            onFinish={(result) => {
              if (isFormVisible) {
                setFormVisible(false);
                const patchedResult = produce(result, (draft) => {
                  draft.reviewScreeningResult!.manual![newReviewIndex].date = dayjs().toISOString();
                  draft.reviewScreeningResult!.manual![newReviewIndex].agentName =
                    decodedToken?.name;
                });
                void saveMutation.mutateAsync(patchedResult);
                form.setFieldsValue(patchedResult);
              } else {
                Modal.confirm({
                  title: t(' Review Summary Page'),
                  content: (
                    <Typography.Paragraph>
                      <p>{t('Would you like to submit screening results?')}</p>
                    </Typography.Paragraph>
                  ),
                  okText: t('Proceed', { ns: 'common' }),
                  cancelText: t('Cancel', { ns: 'common' }),
                  icon: null,
                  onOk() {
                    void submitMutation.mutateAsync(result);
                  },
                });
              }
            }}
          >
            <Form.ShouldUpdate>
              {() => {
                return (
                  !!data?.highRiskReasons?.length && (
                    <Form.Item
                      hidden
                      initialValue={JSON.stringify(data?.highRiskReasons || [])}
                      name={['reviewScreeningResult', 'highRiskReasons']}
                    >
                      <Input />
                    </Form.Item>
                  )
                );
              }}
            </Form.ShouldUpdate>
            <Typography.Title>{t('Review Summary Page')}</Typography.Title>

            {/* Products Summary — row/value layout matching ProductsSummary.tsx (third image) */}
            <ProductSummaryPanel>
              <ProductSectionHeader strong>
                {t('Product Selected')}
              </ProductSectionHeader>

              {
                (() => {
                  const services = payload?.services as any;
                  const isPOS = !!services?.POS?.enabled;
                  const isEcommerce = !!services?.eCommerce?.enabled;
                  const productLabels: string[] = [];
                  if (isPOS) productLabels.push(t('POS Terminal'));
                  if (isEcommerce) productLabels.push(t('E-commerce'));
                  const noOfProducts = productLabels.length;
                  const terminals = services?.POS?.terminals;

                  return (
                    <ProductDetailsWrapper>
                      {/* No of Products — only shown when both products selected */}
                      {noOfProducts > 1 && (
                        <ProductRow>
                          <Col span={12}>
                            <LabelText>
                              {t('No of Products', { ns: 'ksaApplication' })}
                            </LabelText>
                          </Col>
                          <Col span={12}>
                            <ValueText>
                              {noOfProducts}
                            </ValueText>
                          </Col>
                        </ProductRow>
                      )}

                      {/* Product */}
                      <ProductRow>
                        <Col span={12}>
                          <LabelText>
                            {t('Product', { ns: 'ksaApplication' })}
                          </LabelText>
                        </Col>
                        <Col span={12}>
                          <ValueText>
                            {productLabels.length > 0 ? productLabels.join(', ') : '—'}
                          </ValueText>
                        </Col>
                      </ProductRow>

                      {/* No of Terminals — only when POS selected */}
                      {isPOS && (
                        <ProductRow>
                          <Col span={12}>
                            <LabelText>
                              {t('No of Terminals', { ns: 'ksaApplication' })}
                            </LabelText>
                          </Col>
                          <Col span={12}>
                            <ValueText>
                              {terminals ?? '—'}
                            </ValueText>
                          </Col>
                        </ProductRow>
                      )}

                      {/* Automated Screening resolution */}
                      {/* {lastAutomatedResolution && (
                      <ProductRow>
                        <Col span={12}>
                          <LabelText>
                            {t('Automated Screening')}
                          </LabelText>
                        </Col>
                        <Col span={12}>
                          <Tag
                            color={
                              lastAutomatedResolution === 'MATCH' || lastAutomatedResolution === 'NO_MATCHES'
                                ? 'success'
                                : lastAutomatedResolution === 'ERROR'
                                  ? 'error'
                                  : lastAutomatedResolution === 'IN_PROGRESS'
                                    ? 'processing'
                                    : 'default'
                            }
                          >
                            {lastAutomatedResolution}
                          </Tag>
                        </Col>
                      </ProductRow>
                    )} */}
                    </ProductDetailsWrapper>
                  );
                })()
              }
            </ProductSummaryPanel>


            {(() => {
              const services = payload?.services as any;
              const isEcom = !!services?.eCommerce?.enabled;
              const tamaraSelected = !!services?.eCommerce?.tamara;
              const tabbySelected = !!services?.eCommerce?.tabby;
              if (!isEcom || (!tamaraSelected && !tabbySelected)) return null;
              return (
                <AdditionalPaymentPanel dir={dir}>
                  <ProductSectionHeader strong>
                    {t('Additional Payment Options')}
                  </ProductSectionHeader>
                  <AdditionalPaymentItems>
                    {[
                      // { key: 'tamara', label: t('Tamara'), selected: tamaraSelected },
                      { key: 'tabby', label: t('Tabby'), selected: tabbySelected },
                    ].map(({ key, label, selected }) => (
                      <AgentBnplRow key={key} dir={dir}>
                        <AgentRadioCircle selected={selected}>
                          {selected && (
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M1.5 5L3.8 7.5L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </AgentRadioCircle>
                        <AgentBnplTextBlock dir={dir}>
                          <span className="bnpl-label">{label}</span>
                        </AgentBnplTextBlock>
                      </AgentBnplRow>
                    ))}
                  </AdditionalPaymentItems>
                </AdditionalPaymentPanel>
              );
            })()}

            <RiskTagsWrapper>
              {highResikReasons?.map((item: any) => (
                // eslint-disable-next-line react/jsx-no-undef
                <StyledTag
                  key={item?.label}
                  color={item?.status ?? 'default'}
                >
                  {item?.label}
                </StyledTag>
              ))}
              {splAddress && <StyledTag color="warning">
                {t('National Address Missing')}
              </StyledTag>}
            </RiskTagsWrapper>
            {
              screeningResult?.automated &&
              screeningResult?.automated?.map((item) => (
                <AutomatedScreeningResult key={item?.date} result={item} />
              ))
            }

            {
              screeningResult?.manual &&
              newReviewIndex > 0 &&
              screeningResult.manual.map((manualResult, i) => {
                return (
                  <ManualScreeningResultsView
                    result={manualResult}
                    key={manualResult.uid}
                    baseNamePath={manualScreeningResultsFormName(i)}
                  />
                );
              })
            }
            <TriggerButtonContainer>
              <Button
                type="primary"
                block
                loading={isTriggeringScreening}
                onClick={handleTriggerScreening}
                id="trigger-screening-btn"
              >
                {t('Trigger Automated Screening')}
              </Button>
            </TriggerButtonContainer>
            <ManualScreeningAction
              baseNamePath={manualScreeningResultsFormName(newReviewIndex)}
              withCancel
              isFormVisible={isFormVisible}
              onFormVisible={setFormVisible}
            // disabled={
            //   lastAutomatedResolution === 'IN_PROGRESS' || lastAutomatedResolution === 'MANUAL'
            // }
            />
          </WizardForm >
        </Container >
      </Tabs.TabPane >
      {data?.stage !== 'ksaContract' && data?.stage !== 'ksaReview' && (
        <Tabs.TabPane tab={<NarrowTabLabel>{t('Status')}</NarrowTabLabel>} key="2">
          <AgentStatus />
        </Tabs.TabPane>
      )}
      {/* New tab (Risk Team requirement): gives reviewers a single place to see all Thiqah/Lean
          fields that came back missing or in Arabic, so they know which Agent Updates fields to fill in */}
      {/* <Tabs.TabPane
        tab={
          <ExtraWideTabLabel>
            Missing/Wrong Retrieved Data            
            {hasMissingWrongData && (
              <FlagCountTag color="error">
                {flaggedFields.length}
              </FlagCountTag>
            )}
          </ExtraWideTabLabel>
        }
        key="3"
      >
        <FlaggedFieldsTab flaggedFields={flaggedFields} />
      </Tabs.TabPane> */}
    </StyledTabs >
  );
};

export const manualScreeningResultsFormName = (index: number) => [
  'reviewScreeningResult',
  'manual',
  index,
];

interface FlaggedFieldsTabProps {
  flaggedFields: ReturnType<typeof getFlaggedFields>;
}

const FlaggedFieldsTab: FC<FlaggedFieldsTabProps> = ({ flaggedFields }) => {
  const columns = [
    {
      title: 'Section',
      dataIndex: 'section',
      key: 'section',
      width: '30%',
    },
    {
      title: 'Field',
      dataIndex: 'field',
      key: 'field',
      width: '25%',
    },
    {
      title: 'Retrieved Value',
      dataIndex: 'value',
      key: 'value',
      width: '25%',
      render: (value: string) => value || <EmptyValueSpan>—</EmptyValueSpan>,
    },
    {
      title: 'Issue',
      dataIndex: 'issue',
      key: 'issue',
      width: '20%',
      render: (issue: string) => (
        <Tag color="error">{issue === 'missing' ? 'Missing Value' : 'Arabic Value'}</Tag>
      ),
    },
  ];

  if (flaggedFields.length === 0) {
    return (
      <EmptyFlagMessage>
        <Typography.Text type="secondary">
          No missing or incorrect retrieved fields detected.
        </Typography.Text>
      </EmptyFlagMessage>
    );
  }

  return (
    <FlaggedFieldsWrapper>
      <Typography.Paragraph>
        The following fields retrieved from Thiqah or Lean have missing or Arabic values and require
        agent updates:
      </Typography.Paragraph>
      <Table
        dataSource={flaggedFields.map((f, i) => ({ ...f, key: i }))}
        columns={columns}
        pagination={false}
        size="small"
        bordered
      />
    </FlaggedFieldsWrapper>
  );
};

const Container = styled.div``;

const StyledTabs = styled(Tabs)`
          margin-bottom: 20px;
          `;

const WideTabLabel = styled.div`
          width: 20vw;
          `;

const NarrowTabLabel = styled.div`
          width: 10vw;
          `;

const ExtraWideTabLabel = styled.div`
          width: 22vw;
          display: flex;
          align-items: center;
          gap: 6px;
          `;

const FlagCountTag = styled(Tag)`
          margin-left: 4px;
          margin-bottom: 0;
          `;

const TriggerButtonContainer = styled.div`
          margin-top: 16px;
          margin-bottom: 16px;
          `;

const EmptyFlagMessage = styled.div`
          padding: 24px;
          text-align: center;
          `;

const ProductSummaryPanel = styled.div`
          background: #f8f9fa;
          border: 1px solid #e8e8e8;
          border-radius: 6px;
          padding: 14px 16px;
          margin-bottom: 20px;
          `;

const ProductSectionHeader = styled(Typography.Text)`
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 1px;
          `;

const ProductDetailsWrapper = styled.div`
          margin-top: 12px;
          `;

const ProductRow = styled(Row)`
          margin-bottom: 8px;
          `;

const LabelText = styled(Typography.Text)`
          font-weight: 300;
          font-size: 14px;
          `;

const ValueText = styled(Typography.Text)`
          font-weight: 500;
          font-size: 14px;
          `;

const RiskTagsWrapper = styled.div`
          margin-bottom: 24px;
          `;

const StyledTag = styled(Tag)`
          margin-bottom: 8px;
          `;

const EmptyValueSpan = styled.span`
          color: #999;
          font-style: italic;
          `;

const FlaggedFieldsWrapper = styled.div`
          margin-top: 16px;
          `;

// ─── Additional Payment Options panel (Agent Portal – read-only) ──────────────

const AdditionalPaymentPanel = styled.div<{ dir?: string }>`
  background: #f8f9fa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 14px 16px;
  margin-bottom: 20px;
  direction: ${({ dir }) => dir ?? 'ltr'};
`;

const AdditionalPaymentItems = styled.div`
  margin:15px 0px;
  display: flex;
  flex-direction: row;
  gap: 14px;
`;

const AgentBnplRow = styled.div<{ dir?: string }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  direction: ${({ dir }) => dir ?? 'ltr'};
  cursor: no-drop;
`;

const AgentRadioCircle = styled.span<{ selected: boolean }>`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${({ selected }) => (selected ? '#8c8c8c' : '#d9d9d9')};
  background: ${({ selected }) => (selected ? '#8c8c8c' : '#fff')};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
`;

const AgentBnplTextBlock = styled.div<{ dir?: string }>`
  display: flex;
  flex-direction: column;
  gap: 2px;

  .bnpl-label {
    font-size: 14px;
    font-weight: 600;
    color: #1c2027;
    line-height: 20px;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
  }

`;

export default ScreeningResult;
